import type { DatabaseEngineId } from '@/core/engines';
import { DatabaseObject, DatabaseObjectType } from '../models/databaseObjectTypes';
import databaseObjectsService from './databaseObjectsService';
import type { ExplorerFetchOrigin } from './explorerQueryUtils';

interface ObjectFetchConfig {
  fetchFn: (
    connectionId: string,
    database: string,
    engine?: DatabaseEngineId | string,
    origin?: ExplorerFetchOrigin,
  ) => Promise<DatabaseObject[]>;
  nodeType: DatabaseObjectType;
  emptyMessage: string;
}

const objectFetchConfigs: Record<DatabaseObjectType, ObjectFetchConfig> = {
  table: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { tables, error } = await databaseObjectsService.getTables(connectionId, database, engine, origin);
      if (error) throw new Error(error);
      return tables;
    },
    nodeType: 'table',
    emptyMessage: 'No tables',
  },
  view: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { views, error } = await databaseObjectsService.getViews(connectionId, database, engine, origin);
      if (error) throw new Error(error);
      return views;
    },
    nodeType: 'view',
    emptyMessage: 'No views',
  },
  'stored-procedure': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { procedures, error } = await databaseObjectsService.getStoredProcedures(
        connectionId,
        database,
        engine,
        origin,
      );
      if (error) throw new Error(error);
      return procedures;
    },
    nodeType: 'stored-procedure',
    emptyMessage: 'No procedures',
  },
  function: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { functions, error } = await databaseObjectsService.getFunctions(connectionId, database, engine, origin);
      if (error) throw new Error(error);
      return functions;
    },
    nodeType: 'function',
    emptyMessage: 'No functions',
  },
  package: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'package', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'package',
    emptyMessage: 'No packages',
  },
  sequence: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'sequence', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'sequence',
    emptyMessage: 'No sequences',
  },
  synonym: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'synonym', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'synonym',
    emptyMessage: 'No synonyms',
  },
  'db-link': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'db-link', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'db-link',
    emptyMessage: 'No DB links',
  },
  'materialized-view': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'materialized-view', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'materialized-view',
    emptyMessage: 'No materialized views',
  },
  type: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'type', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'type',
    emptyMessage: 'No types',
  },
  'table-type': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'table-type', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'table-type',
    emptyMessage: 'No user-defined table types',
  },
  'alias-type': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'alias-type', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'alias-type',
    emptyMessage: 'No user-defined data types',
  },
  'clr-type': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'clr-type', engine, origin);
      // Msg 208: sys.assembly_types is absent on engines/editions without CLR
      // (Azure SQL Edge). An unavailable catalog view means "no CLR types",
      // not a broken tree — same graceful degradation as ORA-00942 above.
      if (error && /Invalid object name/i.test(error)) return [];
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'clr-type',
    emptyMessage: 'No user-defined CLR types',
  },
  directory: {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'directory', engine, origin);
      // ORA-00942: table or view does not exist — user lacks privileges on ALL_DIRECTORIES
      if (error && error.includes('ORA-00942')) return [];
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'directory',
    emptyMessage: 'No directories',
  },
  'external-table': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'external-table', engine, origin);
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'external-table',
    emptyMessage: 'No external tables',
  },
  'property-graph': {
    fetchFn: async (connectionId, database, engine, origin) => {
      const { objects, error } = await databaseObjectsService.getOracleObjects(connectionId, database, 'property-graph', engine, origin);
      // ORA-00942: ALL_PROPERTY_GRAPHS doesn't exist — server is older than Oracle 23ai
      if (error && error.includes('ORA-00942')) return [];
      if (error) throw new Error(error);
      return objects;
    },
    nodeType: 'property-graph',
    emptyMessage: 'No property graphs',
  },
};

export type FetchObjectsResult = { objects: DatabaseObject[]; error?: string };

/**
 * Fetch database objects for a folder
 * @param objectType Type of objects to fetch
 * @param connectionId Connection ID
 * @param database Database name
 * @param engine Database engine (defaults to 'mssql')
 * @param origin `'preload'` for the background preload (gated on SQL Tools), else the user
 */
export async function fetchObjectsForFolder(
  objectType: DatabaseObjectType,
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin,
): Promise<FetchObjectsResult> {
  const config = objectFetchConfigs[objectType];

  try {
    const objects = await config.fetchFn(connectionId, database, engine, origin);
    return { objects };
  } catch (error) {
    return {
      objects: [],
      error: error instanceof Error ? error.message : 'Unknown error fetching objects',
    };
  }
}

export function getObjectFetchConfig(objectType: DatabaseObjectType) {
  return objectFetchConfigs[objectType];
}

